import React, { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import composeRequestArgs from "./composeRequestArgs";
import { fetchData, FetchDataArgs } from "../utils/fetchData";
import formatPaginationData from "../components/Pagination/formatPaginationData";
import formatSortOrder from "../components/SearchResultsTable/formatSortOrder";
import getPageOffset from "../components/Pagination/getPageOffset";
import {
    DataProviderProps,
    SearchFilters,
    SearchQuestionContextProps,
    SearchResultsContextProps,
    SearchResultsSort,
    SortValues,
} from "../types/context";
import { FunnelbackResponse } from "../types/funnelback";
import composeInitialSort from "./composeInitialSort";
import resolveFacets from "./resolveFacets";
import getQuestionInputParameter from "../utils/getQuestionInputParameter";
import { showAllQuery } from "../constants/showAllQuery";
import { normalizeIncomingQuery } from "../utils/resolveQueryParam";

export { showAllQuery };

const SearchResultsContext = createContext<
    SearchResultsContextProps | Record<string, never>
>({});
const SearchQuestionContext = createContext<
    SearchQuestionContextProps | Record<string, never>
>({});

const DataProvider = ({
    children,
    config,
    maxPages = 5,
}: DataProviderProps) => {
    const [searchParams] = useSearchParams();
    const initialQuery = normalizeIncomingQuery(
        searchParams.get("query"),
        config.collection
    );

    const [response, setResponse] = useState<
        FunnelbackResponse | Record<string, never>
    >({});
    const [defaultResponse, setDefaultResponse] = useState<
        FunnelbackResponse | Record<string, never>
    >({});
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
    const [storedSortOrder, setStoredSortOrder] = useState<SearchResultsSort>({
        field: "",
        direction: "none",
    });
    const [currentStartRank, setCurrentStartRank] = useState<number>(1);
    const [resultsFocusNonce, setResultsFocusNonce] = useState(0);

    const results = response?.response?.resultPacket?.results || [];
    const facets =
        resolveFacets(response?.response?.facets, config.collection) || [];
    const allFacets =
        resolveFacets(defaultResponse?.response?.facets, config.collection) ||
        [];

    const resultsSummary =
        response?.response?.resultPacket?.resultsSummary || {};
    const pagination = formatPaginationData(resultsSummary, maxPages);
    const sortOrder = formatSortOrder(
        getQuestionInputParameter(response?.question, "sort") ?? ""
    );

    const getData = (args?: Omit<FetchDataArgs, "config">) =>
        fetchData({ ...args, config })
            .then((newResponse) => {
                setResponse(newResponse);
                // Store the current sort parameter when search results load
                const rawSort = getQuestionInputParameter(
                    newResponse?.question,
                    "sort"
                );
                if (rawSort) {
                    setStoredSortOrder(formatSortOrder(rawSort));
                }
                // Store the current start_rank when search results load
                if (args?.offset) {
                    setCurrentStartRank(args.offset);
                } else {
                    // If no offset is provided, default to 1 (first page)
                    setCurrentStartRank(1);
                }
            })
            .catch(() => {
                setResponse({});
                return Promise.reject();
            });

    // Set initial response on component mount
    useEffect(() => {
        if (!config?.endpoint || !config?.collection) return;
        const initialQuery = composeRequestArgs(searchParams, config.collection);
        if (config?.initialSort) {
            initialQuery.sort = composeInitialSort(config.initialSort);
        }

        // Set initial start_rank from URL parameters
        const initialStartRank = searchParams.get("start_rank");
        if (initialStartRank) {
            setCurrentStartRank(Number(initialStartRank));
        }

        fetchData({ config })
            .then(setDefaultResponse)
            .catch(() => setDefaultResponse({}));
        getData(initialQuery);
    }, []);

    // Set query helper
    function setQuery(value: string) {
        const queryValue = normalizeIncomingQuery(value, config.collection);
        setSearchQuery(queryValue);
        getData({
            query: queryValue,
            params: searchFilters,
            sort: storedSortOrder,
            offset: 1,
        });
    }

    // Set filter helper
    function setSingleFilter(filter: string, value: string) {
        const filters = {
            ...searchFilters,
            [filter]: value,
        };
        setMultipleFilters(filters);
    }

    // Set multiple facet values
    function setMultipleFilters(filters: SearchFilters) {
        setSearchFilters(filters);
        getData({
            query: searchQuery,
            params: filters,
            sort: storedSortOrder,
            offset: 1,
        });
    }

    function setFormQuestion(formQuery: string, filters: SearchFilters) {
        const queryValue = normalizeIncomingQuery(
            formQuery,
            config.collection
        );
        setSearchQuery(queryValue);
        setSearchFilters(filters);
        getData({
            query: queryValue,
            params: filters,
            sort: storedSortOrder,
            offset: 1,
        });
    }

    // Clear filters helper
    function clearFilters() {
        setSearchFilters({});
        getData({ query: searchQuery, sort: storedSortOrder, offset: 1 });
    }

    // Set pagination
    function setPage(pageNumber: number) {
        // calculate page from offset
        const limit =
            resultsSummary && resultsSummary.numRanks
                ? resultsSummary.numRanks
                : 10;
        const offset = getPageOffset(pageNumber, limit);
        getData({
            query: searchQuery,
            params: searchFilters,
            offset,
            sort: storedSortOrder,
        }).then(() => {
            setResultsFocusNonce((nonce) => nonce + 1);
        }, () => undefined);
    }

    function setSortOrder(field: string, direction: SortValues) {
        const newSort = { field, direction };
        // Update the stored sort parameter when sorting is changed
        setStoredSortOrder(newSort);
        getData({
            query: searchQuery,
            params: searchFilters,
            sort: newSort,
            offset: 1,
        });
    }

    const searchResultsContextValue = {
        config,
        facets,
        allFacets,
        pagination,
        query: response as FunnelbackResponse,
        results,
        resultsSummary,
        currentStartRank,
        resultsFocusNonce,
    };
    const searchQuestionContextValue = {
        filters: searchFilters,
        query: searchQuery,
        sortOrder,
        clearFilters,
        setSingleFilter,
        setMultipleFilters,
        setFormQuestion,
        setPage,
        setQuery,
        setSortOrder,
    };

    return (
        <SearchResultsContext.Provider value={searchResultsContextValue}>
            <SearchQuestionContext.Provider value={searchQuestionContextValue}>
                {children}
            </SearchQuestionContext.Provider>
        </SearchResultsContext.Provider>
    );
};

export const useSearchResults = () => {
    return useContext(SearchResultsContext) as SearchResultsContextProps;
};

export const useSearchQuestion = () => {
    return useContext(SearchQuestionContext) as SearchQuestionContextProps;
};

export default DataProvider;
